// Pure canvas rendering + geometry for markup items. Shared verbatim by the
// live overlay (DrawingLayer) and the flattened export, so what the user sees is
// exactly what gets baked in. `ctx` is a parameter — tests pass a fake, since
// jsdom ships no 2D context.

import { MARKER_ALPHA, type IMarkupPoint, type MarkupColor, type MarkupItem } from './markupTypes';

/** Minimal slice of CanvasRenderingContext2D the renderer needs. */
export type MarkupDrawContext = Pick<
  CanvasRenderingContext2D,
  | 'beginPath' | 'moveTo' | 'lineTo' | 'bezierCurveTo' | 'ellipse' | 'closePath' | 'stroke' | 'strokeRect' | 'fillText'
  | 'fillRect' | 'measureText'
  | 'strokeStyle' | 'fillStyle' | 'lineWidth' | 'lineCap' | 'lineJoin' | 'font' | 'textBaseline' | 'globalAlpha'
  | 'textAlign' | 'direction'
>;

/** The CSS font string for a text item's style — one place, shared with the
 *  PDF rasterizer so both outputs shape identically. */
export function textFontString(fontPx: number, style: { bold?: boolean; italic?: boolean }): string {
  return `${style.italic ? 'italic ' : ''}${style.bold ? 700 : 400} ${fontPx}px sans-serif`;
}

/** Line height inside a text box, in em. */
export const TEXT_LINE_HEIGHT = 1.35;

/**
 * Greedy line wrap to a box width: explicit newlines are hard breaks; Latin
 * wraps on spaces, CJK anywhere (its convention). `measure` abstracts the
 * width source — real canvas metrics for rendering, the char estimate for
 * hit-testing.
 */
export function wrapTextLines(measure: (s: string) => number, text: string, maxWidthPx: number): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    const tokens = paragraph.match(/[\u2e80-\uffff]|[^\s\u2e80-\uffff]+\s*|\s+/g) ?? [];
    let line = '';
    for (const token of tokens) {
      const candidate = line + token;
      if (line !== '' && measure(candidate.trimEnd()) > maxWidthPx) {
        lines.push(line.trimEnd());
        line = token.trimStart();
      } else {
        line = candidate;
      }
    }
    lines.push(line.trimEnd());
  }
  return lines;
}

type ResolveColor = (color: MarkupColor) => string;

const px = (p: IMarkupPoint, width: number, height: number) => ({
  x: (p.x / 100) * width,
  y: (p.y / 100) * height,
});

/** Wrapped lines drawn inside the item's box; alignment and bidi direction
 *  act WITHIN the box. Shared by the live overlay and the flattened export;
 *  the PDF rasterizer mirrors it onto its own canvas. */
function drawTextBox(
  ctx: MarkupDrawContext,
  item: Extract<MarkupItem, { kind: 'text' }>,
  width: number,
  height: number,
  color: string,
): void {
  const fontPx = Math.max(8, item.size * width);
  const boxLeft = (item.x / 100) * width;
  const boxTop = (item.y / 100) * height;
  const boxW = Math.max(1, (item.w / 100) * width);
  const align = item.align ?? 'left';

  ctx.fillStyle = color;
  ctx.font = textFontString(fontPx, item);
  ctx.textBaseline = 'top';
  ctx.textAlign = align;
  ctx.direction = item.direction ?? 'ltr';

  const lines = wrapTextLines((s) => ctx.measureText(s).width, item.text, boxW);
  const lineH = fontPx * TEXT_LINE_HEIGHT;
  lines.forEach((line, i) => {
    if (line === '') return;
    const lineTop = boxTop + i * lineH;
    const anchorX = align === 'center' ? boxLeft + boxW / 2 : align === 'right' ? boxLeft + boxW : boxLeft;
    ctx.fillText(line, anchorX, lineTop);
    if (item.underline) {
      const runWidth = ctx.measureText(line).width;
      const left = align === 'center' ? anchorX - runWidth / 2 : align === 'right' ? anchorX - runWidth : anchorX;
      ctx.fillRect(left, lineTop + fontPx * 1.12, runWidth, Math.max(1, fontPx / 14));
    }
  });

  // Alignment/direction are text-only state — reset so they never bleed into
  // a later consumer of this context.
  ctx.textAlign = 'left';
  ctx.direction = 'ltr';
}

/** Cloud bump size: scale-relative, floored so thin strokes still read as a cloud. */
export function cloudBumpPx(lineWidthPx: number, surfaceWidthPx: number): number {
  return Math.max(lineWidthPx * 4, surfaceWidthPx * 0.02);
}

/** Arrowhead wing tips for a line ending at `to`, sized relative to the shaft. */
export function arrowheadPoints(
  from: IMarkupPoint,
  to: IMarkupPoint,
  width: number,
  height: number,
  lengthPx: number,
): [{ x: number; y: number }, { x: number; y: number }] {
  const a = px(from, width, height);
  const b = px(to, width, height);
  const angle = Math.atan2(b.y - a.y, b.x - a.x);
  const spread = Math.PI / 7;
  return [
    { x: b.x - lengthPx * Math.cos(angle - spread), y: b.y - lengthPx * Math.sin(angle - spread) },
    { x: b.x - lengthPx * Math.cos(angle + spread), y: b.y - lengthPx * Math.sin(angle + spread) },
  ];
}

/** A drag's two corners as a top-left-anchored rectangle (any drag direction). */
export function normalizeRect(a: IMarkupPoint, b: IMarkupPoint): { x: number; y: number; w: number; h: number } {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(b.x - a.x),
    h: Math.abs(b.y - a.y),
  };
}

interface ICloudArc {
  from: { x: number; y: number };
  cp1: { x: number; y: number }; // Cubic control points, bulging outward —
  cp2: { x: number; y: number }; // together they shape a near-semicircle.
  to: { x: number; y: number };
}

/**
 * A revision cloud over a dragged rectangle, as scalloped arcs in pixel
 * space: the perimeter split into chords of ~2×bump, each carrying a
 * semicircular bulge outward (one cubic Bézier per scallop — control offset
 * 4/3 of the radius is the standard single-cubic half-circle). The scallops
 * meet at cusps, the classic revision-cloud look. Shared by the canvas
 * renderer and the PDF export (which serializes them as an SVG path).
 */
export function cloudArcs(a: IMarkupPoint, b: IMarkupPoint, width: number, height: number, bumpPx: number): ICloudArc[] {
  const r = normalizeRect(a, b);
  const left = (r.x / 100) * width;
  const top = (r.y / 100) * height;
  const w = (r.w / 100) * width;
  const h = (r.h / 100) * height;

  // Corner-to-corner walk, clockwise; outward normal per edge.
  const corners = [
    { x: left, y: top }, { x: left + w, y: top },
    { x: left + w, y: top + h }, { x: left, y: top + h },
  ];
  const normals = [
    { x: 0, y: -1 }, { x: 1, y: 0 }, { x: 0, y: 1 }, { x: -1, y: 0 },
  ];

  const arcs: ICloudArc[] = [];
  for (let edge = 0; edge < 4; edge += 1) {
    const from = corners[edge]!;
    const to = corners[(edge + 1) % 4]!;
    const len = Math.hypot(to.x - from.x, to.y - from.y);
    const segments = Math.max(1, Math.round(len / (bumpPx * 2)));
    // The actual half-chord is each scallop's radius, so neighbours always
    // meet in a cusp regardless of how the edge length divides.
    const bulge = (len / segments / 2) * (4 / 3);
    const n = normals[edge]!;
    for (let i = 0; i < segments; i += 1) {
      const p = { x: from.x + ((to.x - from.x) * i) / segments, y: from.y + ((to.y - from.y) * i) / segments };
      const q = { x: from.x + ((to.x - from.x) * (i + 1)) / segments, y: from.y + ((to.y - from.y) * (i + 1)) / segments };
      arcs.push({
        from: p,
        cp1: { x: p.x + n.x * bulge, y: p.y + n.y * bulge },
        cp2: { x: q.x + n.x * bulge, y: q.y + n.y * bulge },
        to: q,
      });
    }
  }
  return arcs;
}

/** A cloud's arcs as an SVG path string (y-down pixel space). */
export function cloudSvgPath(arcs: ICloudArc[]): string {
  if (arcs.length === 0) return '';
  const fmt = (n: number) => Number(n.toFixed(2));
  const start = arcs[0]!.from;
  const curves = arcs.map((s) => `C ${fmt(s.cp1.x)} ${fmt(s.cp1.y)} ${fmt(s.cp2.x)} ${fmt(s.cp2.y)} ${fmt(s.to.x)} ${fmt(s.to.y)}`);
  return `M ${fmt(start.x)} ${fmt(start.y)} ${curves.join(' ')} Z`;
}

/** Draws items onto a `width`×`height` pixel surface. */
export function drawItems(
  ctx: MarkupDrawContext,
  items: readonly MarkupItem[],
  width: number,
  height: number,
  resolveColor: ResolveColor,
): void {
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (const item of items) {
    const color = resolveColor(item.color);

    if (item.kind === 'text') {
      drawTextBox(ctx, item, width, height, color);
      continue;
    }

    const lineWidth = Math.max(1, item.width * width);
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;

    if (item.tool === 'rect') {
      const [a, b] = [item.points[0]!, item.points[1]!];
      const r = normalizeRect(a, b);
      ctx.strokeRect((r.x / 100) * width, (r.y / 100) * height, (r.w / 100) * width, (r.h / 100) * height);
      continue;
    }

    if (item.tool === 'ellipse') {
      // Inscribed in the dragged rectangle: centre at its middle, radii half
      // its sides (an even drag draws a true circle).
      const r = normalizeRect(item.points[0]!, item.points[1]!);
      ctx.beginPath();
      ctx.ellipse(
        ((r.x + r.w / 2) / 100) * width,
        ((r.y + r.h / 2) / 100) * height,
        Math.max(1, (r.w / 200) * width),
        Math.max(1, (r.h / 200) * height),
        0, 0, Math.PI * 2,
      );
      ctx.stroke();
      continue;
    }

    if (item.tool === 'cloud') {
      const arcs = cloudArcs(item.points[0]!, item.points[1]!, width, height, cloudBumpPx(lineWidth, width));
      if (arcs.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(arcs[0]!.from.x, arcs[0]!.from.y);
      for (const arc of arcs) ctx.bezierCurveTo(arc.cp1.x, arc.cp1.y, arc.cp2.x, arc.cp2.y, arc.to.x, arc.to.y);
      ctx.closePath();
      ctx.stroke();
      continue;
    }

    // One path per stroke, so a translucent marker doesn't double-darken
    // where it crosses itself.
    if (item.tool === 'marker') ctx.globalAlpha = MARKER_ALPHA;
    ctx.beginPath();
    const first = px(item.points[0]!, width, height);
    ctx.moveTo(first.x, first.y);
    for (const p of item.points.slice(1)) {
      const q = px(p, width, height);
      ctx.lineTo(q.x, q.y);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;

    if (item.tool === 'arrow' && item.points.length >= 2) {
      const from = item.points[0]!;
      const to = item.points[item.points.length - 1]!;
      const head = px(to, width, height);
      const wings = arrowheadPoints(from, to, width, height, lineWidth * 4);
      ctx.beginPath();
      ctx.moveTo(wings[0].x, wings[0].y);
      ctx.lineTo(head.x, head.y);
      ctx.lineTo(wings[1].x, wings[1].y);
      ctx.stroke();
    }
  }
}